/**
 * Comprehensive Session Management Utility
 * Handles different session termination scenarios with proper error handling
 */

import { SessionsApi, Configuration, MultiTenantSessionDto, TenantAccessDto } from './generated-api';
import { MultiTenantSessionDtoFromJSON } from './generated-api/models/MultiTenantSessionDto';
import { createCsrfEnabledConfig } from './csrf-enabled-client';
import { normalizeApiBaseUrl, getApiContextPath } from './utils/url-normalization';
import { logger } from './logger';
import { setActiveSessionId, extractSessionId, parseSessionIdFromSetCookie } from './session-id-store';
import { persistAccessToken, clearPersistedAccessToken } from './access-token-store';

export interface SessionValidationResult {
  isValid: boolean;
  reason?: 'expired' | 'revoked' | 'error' | 'permission_denied' | 'token_not_ready';
  message?: string;
  errorData?: any;
}

export interface SessionTerminationResult {
  success: boolean;
  type: 'user_logout' | 'admin_revocation' | 'expiration' | 'cleanup';
  message: string;
  errorCode?: number;
}

/**
 * Request deduplication cache for session validation
 * Maps sessionId -> Promise<SessionValidationResult>
 * Prevents duplicate concurrent validation requests from multiple components
 */
const validationCache = new Map<string, Promise<SessionValidationResult>>();

/**
 * Cleanup function to remove completed requests from cache
 * Uses a short delay to handle rapid successive calls
 */
function cleanupValidationCache(sessionId: string, delay: number = 100) {
  setTimeout(() => {
    validationCache.delete(sessionId);
    logger.debug(' [SESSION MGMT] Cleaned up validation cache entry');
  }, delay);
}

/**
 * Get the JWT token from sessionStorage (preferred) or localStorage (fallback)
 * This matches the storage strategy used in lib/auth.tsx
 */
function getAuthToken(): string | null {
  try {
    // Check sessionStorage first (preferred), then localStorage (fallback)
    const authTokenData = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : null)
      || (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
    
    if (!authTokenData) return null;
    
    const parsed = JSON.parse(authTokenData);
    return parsed.access_token || null;
  } catch (error) {
    console.error(' [SESSION MGMT] Failed to parse auth token');
    return null;
  }
}

/**
 * Validate session with request deduplication
 * If multiple components call this simultaneously with the same sessionId,
 * only one actual API call is made and all callers share the result.
 * 
 * Performance impact: Reduces backend load by 75% for concurrent requests
 * (e.g., 4 components mounting → 1 API call instead of 4)
 */
export async function validateSession(sessionId: string): Promise<SessionValidationResult> {
  // Check if there's already an in-flight request for this sessionId
  const cachedPromise = validationCache.get(sessionId);
  if (cachedPromise) {
    logger.debug(' [SESSION MGMT] Reusing in-flight validation request');
    return cachedPromise;
  }
  
  // Create new validation request
  logger.debug(' [SESSION MGMT] Creating new validation request');
  const validationPromise = validateSessionInternal(sessionId);
  
  // Cache the promise (not the result - the promise itself!)
  // This ensures all concurrent callers await the same network request
  validationCache.set(sessionId, validationPromise);
  
  // Clean up after completion (success or failure)
  validationPromise.finally(() => {
    cleanupValidationCache(sessionId);
  });
  
  return validationPromise;
}

/**
 * Internal validation function - performs the actual API call
 * This is called by validateSession() which handles request deduplication
 */
async function validateSessionInternal(sessionId: string): Promise<SessionValidationResult> {
  try {
    if (!sessionId) {
      logger.warn(' [SESSION MGMT] No sessionId provided for validation');
      return { isValid: false, reason: 'error', message: 'No session ID provided' };
    }
    
    // Check if sessionId is actually the function name or some other invalid value
    if (sessionId === 'validate' || sessionId === 'validateSession') {
      logger.error(' [SESSION MGMT] Invalid session ID shape');
      return { isValid: false, reason: 'error', message: 'Invalid session ID provided' };
    }
    
    const authToken = getAuthToken();
    if (!authToken) {
      logger.warn(' [SESSION MGMT] No auth token available');
      return { isValid: false, reason: 'token_not_ready', message: 'No authentication token available' };
    }
    
    logger.debug(' [SESSION MGMT] Sending session validation request');
    
    // Use the generated API client with CSRF middleware
    const apiBaseURL = process.env.NEXT_PUBLIC_API_BASE_URL as string;
    const config = createCsrfEnabledConfig(authToken, apiBaseURL);
    const sessionsApi = new SessionsApi(config);
    
    try {
      const response = await sessionsApi.validateSession({ sessionId });
      logger.debug(' [SESSION MGMT] Validation response received');
      
      // Check the actual valid field from the API response
      if (response.valid === false) {
        // Session is explicitly marked as invalid
        const message = response.error || 'Session is invalid';
        let reason = (response.reason || 'error') as 'expired' | 'revoked' | 'error';
        if (!response.reason && /expired/i.test(message)) {
          reason = 'expired';
        }
        logger.debug(' [SESSION MGMT] Session marked as invalid by API, reason:', reason);
        return {
          isValid: false,
          reason,
          message
        };
      }
      
      // If valid is true or undefined (and no error), treat as valid
      logger.log(' [SESSION MGMT] Validation successful with generated API client');
      return { isValid: true };
    } catch (error: any) {
      // FetchError = request failed before a response (e.g. network down, CORS, or middleware threw).
      // Common in dev when backend is not ready or interceptors fail; rare in prod.
      const isFetchError =
        error?.name === 'FetchError' ||
        error?.constructor?.name === 'FetchError' ||
        (error?.message && String(error.message).includes('interceptors did not return'));

      if (isFetchError) {
        logger.log(
          ' [SESSION MGMT] Validation failed: network or interceptor error (no HTTP response). Treating as valid to avoid false negatives. Common in dev when backend is starting or CORS/network issues.',
          error?.cause ?? error
        );
        return { isValid: true };
      }

      logger.log(' [SESSION MGMT] Validation failed with generated API client:', error);

      // Normalize status/response shape across runtime errors
      const normalizedStatus: number | undefined = (error?.status ?? error?.response?.status);

      // Handle different error types
      if (normalizedStatus === 401) {
        return {
          isValid: false,
          reason: 'expired',
          message: 'Your session has expired. Please log in again.'
        };
      } else if (normalizedStatus === 403) {
        // Check if this is a permission-related 403 (user has no permissions) vs session revocation
        const errorMessage = error?.body?.message || error?.message || '';
        if (errorMessage.toLowerCase().includes('permission') || 
            errorMessage.toLowerCase().includes('forbidden') ||
            errorMessage.toLowerCase().includes('access denied')) {
          logger.log(' [SESSION MGMT] Session validation returned 403 (permission denied) - user has no permissions, treating as valid session');
          return {
            isValid: true,
            reason: 'permission_denied',
            message: 'Session is valid but user has no permissions.'
          };
        } else {
          logger.log(' [SESSION MGMT] Session validation returned 403 - session likely cleaned up or revoked');
          return {
            isValid: false,
            reason: 'revoked',
            message: 'Your session has been cleaned up or revoked. Please log in again.',
            errorData: error.body
          };
        }
      } else if (normalizedStatus === 404) {
        logger.log(' [SESSION MGMT] Session validation endpoint not found (404), treating as valid');
        return { isValid: true };
      } else {
        // normalizedStatus is undefined when there was no HTTP response (e.g. network error).
        logger.log(
          ' [SESSION MGMT] Session validation failed with status:',
          normalizedStatus ?? 'none (no HTTP response)',
          '- treating as valid to avoid false negatives. If this persists in prod, check network and backend.'
        );
        return { isValid: true };
      }
    }
  } catch (error) {
    console.error(' [SESSION MGMT] Session validation failed:', error);
    // Network error - treat as valid session to avoid false negatives
    return { isValid: true };
  }
}

/**
 * User-initiated logout
 */
export async function userLogout(sessionId: string): Promise<SessionTerminationResult> {
  try {
    logger.debug(' [SESSION MGMT] User logging out');
    
    const authToken = getAuthToken();
    if (!authToken) {
      return {
        success: false,
        type: 'user_logout',
        message: 'No authentication token available.'
      };
    }
    
    const baseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL as string).replace(/\/$/, '');
    const contextPath = getApiContextPath();
    const { apiBase } = normalizeApiBaseUrl(baseUrl, { contextPath });
    const response = await fetch(`${apiBase}/v1/auth/logout`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ sessionId })
    });
    
    if (response.status === 200) {
      // Clear local session data
      clearSessionData();
      
      return {
        success: true,
        type: 'user_logout',
        message: 'Successfully logged out. You can log back in immediately.'
      };
    } else {
      return {
        success: false,
        type: 'user_logout',
        message: 'Logout failed. Please try again.',
        errorCode: response.status
      };
    }
  } catch (error) {
    console.error(' [SESSION MGMT] User logout failed:', error);
    return {
      success: false,
      type: 'user_logout',
      message: 'Logout failed due to network error.'
    };
  }
}

/**
 * Admin-initiated session revocation
 */
export async function revokeSession(sessionId: string, adminSessionId: string): Promise<SessionTerminationResult> {
  try {
    logger.debug(' [SESSION MGMT] Admin revoking session');
    
    const authToken = getAuthToken();
    if (!authToken) {
      return {
        success: false,
        type: 'admin_revocation',
        message: 'No authentication token available.'
      };
    }
    
    const baseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL as string).replace(/\/$/, '');
    const contextPath = getApiContextPath();
    const { apiBase } = normalizeApiBaseUrl(baseUrl, { contextPath });
    const response = await fetch(`${apiBase}/v1/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });
    
    if (response.status === 200) {
      return {
        success: true,
        type: 'admin_revocation',
        message: 'Session revoked successfully.'
      };
    } else if (response.status === 403) {
      return {
        success: false,
        type: 'admin_revocation',
        message: 'You do not have permission to revoke this session.',
        errorCode: 403
      };
    } else if (response.status === 404) {
      return {
        success: false,
        type: 'admin_revocation',
        message: 'Session not found or already terminated.',
        errorCode: 404
      };
    } else {
      return {
        success: false,
        type: 'admin_revocation',
        message: 'Failed to revoke session.',
        errorCode: response.status
      };
    }
  } catch (error) {
    console.error(' [SESSION MGMT] Session revocation failed:', error);
    return {
      success: false,
      type: 'admin_revocation',
      message: 'Session revocation failed due to network error.'
    };
  }
}

/**
 * Cleanup expired sessions
 */
export async function cleanupExpiredSessions(adminSessionId: string): Promise<SessionTerminationResult> {
  try {
    logger.log(' [SESSION MGMT] Cleaning up expired sessions');
    
    const authToken = getAuthToken();
    if (!authToken) {
      return {
        success: false,
        type: 'cleanup',
        message: 'No authentication token available.'
      };
    }
    
    const baseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL as string).replace(/\/$/, '');
    const contextPath = getApiContextPath();
    const { apiBase } = normalizeApiBaseUrl(baseUrl, { contextPath });
    const response = await fetch(`${apiBase}/v1/sessions/cleanup`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ sessionId: adminSessionId })
    });
    
    if (response.status === 200) {
      const result = await response.json();
      return {
        success: true,
        type: 'cleanup',
        message: `Successfully cleaned up ${result.cleanedCount || 0} expired sessions.`
      };
    } else {
      return {
        success: false,
        type: 'cleanup',
        message: 'Failed to cleanup expired sessions.',
        errorCode: response.status
      };
    }
  } catch (error) {
    console.error(' [SESSION MGMT] Session cleanup failed:', error);
    return {
      success: false,
      type: 'cleanup',
      message: 'Session cleanup failed due to network error.'
    };
  }
}

/**
 * SECURITY (pentest finding C01 core fix): refresh the short-lived Aegis-signed session access
 * token (see AegisSessionTokenService, ~10 minute TTL) via POST /v1/sessions/{sessionId}/token,
 * independent of the external OIDC identity provider's token lifetime. Must be called with the
 * CURRENT (not yet expired) access token - an already-expired token cannot refresh itself; the
 * caller must fully re-authenticate via session create in that case.
 */
export async function refreshSessionAccessToken(sessionId: string): Promise<{
  success: boolean;
  accessToken?: string;
  accessTokenExpiresAt?: string;
  session?: MultiTenantSessionDto;
}> {
  try {
    if (!sessionId) {
      return { success: false };
    }
    const authToken = getAuthToken();
    if (!authToken) {
      logger.warn('[SESSION MGMT] Cannot refresh access token: no current token available');
      return { success: false };
    }

    const baseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL as string).replace(/\/$/, '');
    const contextPath = getApiContextPath();
    const { apiBase } = normalizeApiBaseUrl(baseUrl, { contextPath });
    const response = await fetch(`${apiBase}/v1/sessions/${sessionId}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      logger.warn('[SESSION MGMT] Access token refresh failed with status:', response.status);
      return { success: false };
    }

    const body = await response.json();
    // Raw fetch response uses the wire (snake_case) shape - run it through the generated
    // FromJSON mapper so accessToken/accessTokenExpiresAt (and everything else) are populated.
    const data = body?.data ? MultiTenantSessionDtoFromJSON(body.data) : undefined;
    if (!body?.success || !data?.accessToken) {
      logger.warn('[SESSION MGMT] Access token refresh response missing accessToken');
      return { success: false };
    }

    persistAccessToken(data.accessToken);
    logger.debug('[SESSION MGMT] Access token refreshed successfully');
    return {
      success: true,
      accessToken: data.accessToken,
      accessTokenExpiresAt: data.accessTokenExpiresAt ? data.accessTokenExpiresAt.toISOString() : undefined,
      session: data,
    };
  } catch (error) {
    console.error('[SESSION MGMT] Access token refresh failed:', error);
    return { success: false };
  }
}

/**
 * Clear session data from localStorage
 */
export function clearSessionData(): void {
  logger.log(' [SESSION MGMT] Clearing session data');
  setActiveSessionId(null);
  clearPersistedAccessToken();
  localStorage.removeItem('session');
  localStorage.removeItem('availableTenants');
  localStorage.removeItem('currentTenant');
  localStorage.removeItem('authToken');
  localStorage.removeItem('preferred_tenant_id');
}

/**
 * Synchronously persist session state to localStorage so X-Session-Id headers match
 * the server session immediately after createSession (before React useEffect runs).
 */
export function persistSessionToStorage(data: {
  session: MultiTenantSessionDto | null | undefined;
  availableTenants?: TenantAccessDto[] | null;
  currentTenant?: TenantAccessDto | null;
}): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { session, availableTenants, currentTenant } = data;
  const sessionId = extractSessionId(session);

  if (sessionId) {
    setActiveSessionId(sessionId);
    const toStore = session?.sessionId ? session : { ...session, sessionId };
    localStorage.setItem('session', JSON.stringify(toStore));
    logger.debug('[SESSION MGMT] Persisted session');
  } else if (session) {
    logger.error('[SESSION MGMT] persistSessionToStorage: session object missing sessionId');
  }

  // SECURITY (pentest finding C01 core fix): every session response that carries a fresh
  // Aegis-signed access token must immediately replace whatever token is currently stored -
  // stale OIDC tokens or a previous session's Aegis token must never keep being sent.
  if (session?.accessToken) {
    persistAccessToken(session.accessToken);
  }

  if (availableTenants != null) {
    if (availableTenants.length > 0) {
      localStorage.setItem('availableTenants', JSON.stringify(availableTenants));
    } else {
      localStorage.removeItem('availableTenants');
    }
  }

  if (currentTenant != null) {
    if (currentTenant.tenantId) {
      localStorage.setItem('currentTenant', JSON.stringify(currentTenant));
    } else {
      localStorage.removeItem('currentTenant');
    }
  }
}

/** Resolve session id from createSession body and optional Set-Cookie (HttpOnly cookie sync). */
export function resolveCreatedSessionId(
  session: MultiTenantSessionDto | null | undefined,
  setCookieHeader?: string | null
): string | null {
  return extractSessionId(session) ?? parseSessionIdFromSetCookie(setCookieHeader);
}

/**
 * Synchronously apply a freshly created session to memory + localStorage before any await.
 */
export function applyCreatedSessionToClient(data: {
  session: MultiTenantSessionDto;
  availableTenants?: TenantAccessDto[] | null;
  currentTenant?: TenantAccessDto | null;
  setCookieHeader?: string | null;
}): MultiTenantSessionDto {
  let { session } = data;
  const sessionId = resolveCreatedSessionId(session, data.setCookieHeader);
  if (sessionId && !session.sessionId) {
    session = { ...session, sessionId };
  }
  if (sessionId) {
    setActiveSessionId(sessionId);
  } else {
    console.error('[SESSION MGMT] applyCreatedSessionToClient: missing sessionId', session);
  }
  persistSessionToStorage({
    session,
    availableTenants: data.availableTenants ?? session.availableTenants,
    currentTenant: data.currentTenant ?? session.currentTenant,
  });
  return session;
}

/**
 * Show appropriate UI message based on session termination type
 */
export function showSessionMessage(result: SessionValidationResult | SessionTerminationResult): void {
  if ('isValid' in result) {
    // SessionValidationResult
    if (!result.isValid) {
      if (result.reason === 'expired') {
        showSessionExpiredModal(result.message || 'Session expired');
      } else if (result.reason === 'revoked') {
        showSessionRevokedModal(result.message || 'Session revoked', result.errorData);
      } else {
        showSessionErrorModal(result.message || 'Session error');
      }
    }
  } else {
    // SessionTerminationResult
    if (result.success) {
      showSuccessToast(result.message);
    } else {
      showErrorToast(result.message);
    }
  }
}

/**
 * Show session expired modal
 */
function showSessionExpiredModal(message: string): void {
  const modal = createModal({
    title: 'Session Expired',
    message,
    type: 'warning',
    icon: '',
    actions: [
      { text: 'Log In', action: () => redirectToLogin() }
    ]
  });
  document.body.appendChild(modal);
}

/**
 * Show session revoked modal
 */
function showSessionRevokedModal(message: string, errorData?: any): void {
  const revocationReason = errorData?.revocationReason || 'Security policy violation';
  const modal = createModal({
    title: 'Session Revoked',
    message: `${message}\n\nReason: ${revocationReason}`,
    type: 'error',
    icon: '🚫',
    actions: [
      { text: 'Contact Admin', action: () => redirectToContactAdmin() }
    ]
  });
  document.body.appendChild(modal);
}

/**
 * Show session error modal
 */
function showSessionErrorModal(message: string): void {
  const modal = createModal({
    title: 'Session Error',
    message,
    type: 'error',
    icon: '❌',
    actions: [
      { text: 'Try Again', action: () => window.location.reload() },
      { text: 'Go to Login', action: () => redirectToLogin() }
    ]
  });
  document.body.appendChild(modal);
}

/**
 * Create modal element
 */
function createModal({ title, message, type, icon, actions }: {
  title: string;
  message: string;
  type: 'warning' | 'error' | 'success';
  icon: string;
  actions: Array<{ text: string; action: () => void }>;
}): HTMLElement {
  const modal = document.createElement('div');
  modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
  
  const bgColor = type === 'error' ? 'bg-red-500' : type === 'warning' ? 'bg-amber-500' : 'bg-green-500';
  
  modal.innerHTML = `
    <div class="bg-background border border-surface-dark/50 rounded-lg p-6 max-w-md w-full mx-4">
      <div class="flex items-center space-x-3 mb-4">
        <div class="w-8 h-8 ${bgColor} rounded-full flex items-center justify-center">
          <span class="text-white text-lg">${icon}</span>
        </div>
        <h3 class="text-lg font-semibold text-white">${title}</h3>
      </div>
      <p class="text-slate-300 mb-6 whitespace-pre-line">${message}</p>
      <div class="flex justify-end space-x-2">
        ${actions.map((action, index) => `
          <button id="action-${index}" class="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700">
            ${action.text}
          </button>
        `).join('')}
      </div>
    </div>
  `;
  
  // Add event listeners
  actions.forEach((action, index) => {
    modal.querySelector(`#action-${index}`)?.addEventListener('click', () => {
      document.body.removeChild(modal);
      action.action();
    });
  });
  
  return modal;
}

/**
 * Show success toast
 */
function showSuccessToast(message: string): void {
  // You can integrate with your toast system here
  logger.log(' Success:', message);
}

/**
 * Show error toast
 */
function showErrorToast(message: string): void {
  // You can integrate with your toast system here
  console.error('❌ Error:', message);
}

/**
 * Redirect to login page
 */
function redirectToLogin(): void {
  clearSessionData();
  window.location.href = '/';
}

/**
 * Redirect to contact admin page
 */
function redirectToContactAdmin(): void {
  clearSessionData();
  window.location.href = '/contact-admin';
} 